export const FULL_TOUR_VERSION = '2026-05-03-r220';
export const ESSENTIALS_TOUR_VERSION = '2026-07-28-essentials-r1';
export const FULL_JOURNEY_VERSION = 'full-v1';
export const ESSENTIALS_JOURNEY_VERSION = 'essentials-v1';

const FULL_TOUR_STEPS = [
  { title: 'Welcome to EliteEdge AI!', description: 'Your AI-powered coaching workspace. Let us show you where everything lives.', target: null },
  { title: 'Set Your Club Coaching Philosophy', description: 'Open Settings (top-right \u2699 on desktop, or \u2699 inside the menu on mobile) and write your Club Coaching Philosophy \u2014 your tactical style, development priorities, and key principles. The AI weaves this into every chat reply, scheduled report, match recap, and training session it generates, so it shapes every AI output across the product. You can layer on a team-specific philosophy in Team Manager later.', target: '[data-tour="settings-button"]', mobileTarget: null },
  { title: 'Dashboard', description: 'See your home dashboard with your coaching story, smart guide, and quick links into the rest of the app.', target: '[data-tour="nav-dashboard"]' },
  { title: 'Session Builder', description: 'Generate complete AI-powered sessions in seconds using your focus, time, and player context.', target: '[data-tour="nav-training"]' },
  { title: "Today's Practice", description: "Run your live session with attendance, a built-in timer, and drill progress tracking. After each session, rate it 1\u201310 with the new Practice Scoreboard and your last 3 ratings show up so trends are obvious.", target: '[data-tour="nav-today-practice"]', mobileTarget: '[data-tour="nav-today-practice"]' },
  { title: 'Calendar', description: 'Schedule training and matches. Adding a match here opens the same form as Team Manager, with opponent, venue, timezone, and kickoff preview.', target: '[data-tour="nav-calendar"]' },
  { title: 'Season Planner / Training Blocks', description: 'Map out training blocks across your season so every session links back to long-term goals.', target: null },
  { title: 'Scout Library', description: 'Browse drills, scouting reports, and session ideas for different ages, levels, and coaching themes.', target: '[data-tour="nav-scout"]' },
  { title: 'Squad Roster', description: 'Manage your players, upload photos, and track custom stats. Tap any player card to open their folder with stats, reports, targets, and exportable DOCX reports. Import via CSV to get started quickly.', target: '[data-tour="nav-roster"]' },
  { title: 'Team Profile', description: 'View aggregate team stats and targets so your training plans stay aligned with what the team needs most.', target: null },
  { title: 'Team Manager', description: 'Record fixtures, then after each match tap Add score & subs to log the final score, goal scorers, and per-player match stats. Right under the Notes box you will see \u2728 Expand with AI \u2014 once you have filled the score, formation, lineup, and 20+ characters of notes, tap it for a structured 5-section recap. Tip: when you create or edit a team here, fill in the Team Philosophy field \u2014 that team-specific philosophy overrides your club-level one for AI sessions, recaps, and scouting reports tied to that team.', target: null },
  { title: 'Film Room', description: 'Upload or link match video for AI tactical breakdowns, key moments, and animated insights.', target: '[data-tour="nav-film"]' },
  { title: 'Tactics Board', description: 'Draw and animate tactics, design drills, and use AI to generate new scenarios and patterns of play.', target: '[data-tour="nav-tactics-board"]', mobileTarget: '[data-tour="nav-tactics-board"]' },
  { title: 'AI Coach Chat', description: 'Ask coaching questions, generate ideas, and refine plans with 5 AI personas, voice input, upcoming-match context, and landscape formation diagrams. You can also ask it to schedule training or matches \u2014 it confirms a plain-English summary before anything hits the calendar.', target: '[data-tour="nav-chat"]' },
  { title: 'Club Chat', description: 'Coordinate with your staff and share updates in team channels and direct messages.', target: '[data-tour="nav-club-chat"]' },
  { title: 'You are all set!', description: 'Start by creating your team, planning a session, or dropping a video into Film Room. Happy coaching! \uD83C\uDFC6', target: null },
];

const FULL_JOURNEY_STEPS = [
  {
    id: 'philosophy',
    title: 'Set Your Club Coaching Philosophy',
    description: 'Before anything else, open Settings (top-right \u2699) and write your Club Coaching Philosophy \u2014 your tactical style, development priorities, and key principles. The AI weaves this into every chat reply, scheduled report, match recap, and training session, so it shapes every AI output. You can add a Team Philosophy later for any team that differs from your club-level one.',
    features: 'Club philosophy (Settings) \u2022 Team philosophy (Team Manager later) \u2022 Used everywhere AI runs',
    action: 'openSettings',
    icon: '\uD83E\uDDED',
  },
  {
    id: 'team',
    title: 'Create or Join a Team',
    description: 'Set up your first team so EliteEdge can tailor sessions, analysis, and stats to your squad.',
    features: 'Team setup \u2022 Join via invite \u2022 Club link',
    path: '/team',
    action: 'teamManager',
    icon: '\uD83D\uDC65',
  },
  {
    id: 'roster',
    title: 'Add Players to Your Roster',
    description: 'Add players to your squad manually or import a CSV so your roster is ready for training and matches.',
    features: 'Player list \u2022 Positions \u2022 CSV import',
    path: '/roster',
    icon: '\uD83D\uDCCB',
  },
  {
    id: 'stats',
    title: 'Set Up Team Stats',
    description: 'Choose the stats your team tracks \u2014 goals, assists, tackles, or your own custom ones \u2014 so match entry and player reports match how you coach.',
    features: 'Goals & assists \u2022 Custom stats \u2022 Player reports',
    path: '/roster?setup=stats',
    icon: '\uD83D\uDCC8',
  },
  {
    id: 'photos',
    title: 'Upload Player Photos',
    description: 'Upload photos for each player so lineups, reports, and analysis are easier to read and share.',
    features: 'Profile photos \u2022 Roster visuals',
    path: '/roster',
    icon: '\uD83D\uDDBC\uFE0F',
  },
  {
    id: 'match',
    title: 'Capture Your First Match Story',
    description: 'Turn a played fixture into a complete record \u2014 final score, goal scorers, and how each player performed. Tap Add score & subs on the match card; once you have entered the score, formation, lineup, and 20+ characters of notes, tap \u2728 Expand with AI right under the Notes box for a 5-section structured recap.',
    features: 'Final score \u2022 Goal scorers \u2022 Per-player stats \u2022 \u2728 AI recap expander',
    path: '/team',
    action: 'teamManager',
    icon: '\uD83D\uDCCA',
  },
  {
    id: 'training',
    title: 'Generate Your First AI Session',
    description: 'Use Session Builder to create a full training plan with drills, timings, and coaching points. After you run it, rate the session 1\u201310 with the new Practice Scoreboard so trends become visible.',
    features: 'AI session plans \u2022 Focus areas \u2022 Timings \u2022 1\u201310 scoreboard',
    path: '/training',
    icon: '\uD83C\uDFC3',
  },
  {
    id: 'drill',
    title: 'Draw a Drill Diagram',
    description: 'Sketch out a key drill or pattern using the tactics tools so you can reuse and share it later.',
    features: 'Drawing tools \u2022 Pitch templates',
    path: '/tactics-board',
    icon: '\u270F\uFE0F',
  },
  {
    id: 'tactics',
    title: 'Explore the Tactics Board',
    description: 'Animate movements, try AI-generated scenarios, and save your favorite tactical ideas.',
    features: 'Animations \u2022 AI scenarios \u2022 Templates',
    path: '/tactics-board',
    icon: '\uD83C\uDFAF',
  },
  {
    id: 'chat',
    title: 'Try the AI Coach Chat',
    description: 'Ask a coaching question, pick a persona, and see how the AI can support your next session or match \u2014 including upcoming-match context, landscape formation diagrams, and natural-language scheduling.',
    features: 'AI personas \u2022 Match context \u2022 Formation diagrams \u2022 Voice input \u2022 AI scheduling',
    path: '/chat',
    icon: '\uD83E\uDD16',
  },
  {
    id: 'calendar',
    title: 'Set Up the Calendar',
    description: 'Add upcoming training and matches so your season schedule stays organized. Adding a match opens the same form as Team Manager, with kickoff times shown in your local timezone.',
    features: 'Calendar \u2022 Weekly view \u2022 Shared match form',
    path: '/calendar',
    icon: '\uD83D\uDCC5',
  },
  {
    id: 'film',
    title: 'Analyze a Video in Film Room',
    description: 'Upload or link a match video and let the AI highlight key moments and tactical themes.',
    features: 'Video upload \u2022 AI tactical analysis',
    path: '/film-room',
    icon: '\uD83C\uDFA9',
  },
];

function essentialsTourSteps(terms, canUse) {
  const training = terms.training || 'training';
  const match = terms.match || 'match';
  const roster = terms.Roster || 'Squad';
  const today = terms.todaySession || "Today's Training";
  const steps = [
    {
      title: 'Welcome \u2014 build confidence one session at a time',
      description: 'This short tour points you to the seven places you will use most. When it finishes, your coaching checklist opens to guide the real setup.',
      target: null,
    },
    {
      title: 'Define your coaching philosophy',
      description: `Open Settings and write the principles you want every ${training} and ${match} plan to follow. Then choose your region so wording matches your game.`,
      target: '[data-tour="settings-button"]',
      mobileTarget: null,
    },
    {
      title: today,
      description: 'This is your daily coaching home. Mark attendance, run drills, rate the session 1\u201310, and finish with a reflection.',
      target: '[data-tour="nav-today-practice"]',
      mobileTarget: '[data-tour="nav-today-practice"]',
    },
    {
      title: `Add players to your ${roster.toLowerCase()}`,
      description: `Add players and positions so every ${training} plan can use the right age, numbers, and player context.`,
      target: '[data-tour="nav-roster"]',
    },
    {
      title: `Plan one clear ${training} objective`,
      description: 'Session Builder turns one objective into a progressive plan with timings, coaching points, and adaptations.',
      target: '[data-tour="nav-training"]',
    },
    {
      title: `Schedule ${training} and ${match} commitments`,
      description: 'Calendar keeps the week organised. Times always follow the device you are currently using.',
      target: '[data-tour="nav-calendar"]',
    },
    {
      title: 'Use AI Coach as a reflective assistant',
      description: 'Ask why an activity worked, how to adapt it for your players, or what to change next time. You remain the coaching decision-maker.',
      target: '[data-tour="nav-chat"]',
    },
  ];
  if (canUse('club_chat')) {
    steps.push({
      title: 'Keep everyone aligned',
      description: 'Use Club Chat for coaches and staff, and Member Messages for parents or members. Share the next action, not just information.',
      target: '[data-tour="nav-club-chat"]',
    });
  } else {
    steps.push({
      title: 'Keep members aligned',
      description: 'Use Member Messages to share the next action, arrival details, or a concise session update.',
      target: null,
    });
  }
  return steps;
}

function essentialsJourneySteps(terms) {
  const training = terms.training || 'training';
  const match = terms.match || 'match';
  const roster = terms.roster || 'squad';
  const Roster = terms.Roster || 'Squad';
  return [
    {
      id: 'foundation',
      stage: '1. Build your coaching foundation',
      title: 'Define your coaching philosophy',
      description: 'Write the principles that should shape every plan and coaching decision.',
      features: 'Settings \u2022 Club coaching philosophy',
      action: 'openSettings',
      icon: '\uD83E\uDDED',
      why: 'A clear philosophy makes your decisions consistent when sessions, results, or player needs change.',
      goodLooksLike: 'Three to five practical principles describing how you want players to learn, behave, attack, defend, and respond to mistakes.',
      example: 'We develop brave decision-makers through game-based learning, quick transitions, and specific positive feedback.',
      commonMistake: 'Writing broad values such as "work hard" without explaining what coaches and players should actually do.',
    },
    {
      id: 'team',
      stage: '1. Build your coaching foundation',
      title: 'Create or select your team',
      description: `Confirm the age group and team context used for ${training}, ${match}, and AI recommendations.`,
      features: 'Team context \u2022 Age group \u2022 Club link',
      action: 'teamManager',
      icon: '\uD83D\uDC65',
      why: 'Age, format, and team context determine appropriate space, complexity, physical load, and language.',
      goodLooksLike: 'The correct age group, sport, team name, and active team are selected before planning.',
      example: 'An U10 activity uses fewer rules, smaller spaces, and more ball contacts than a senior tactical session.',
      commonMistake: 'Reusing the same activity and coaching detail for every age group.',
    },
    {
      id: 'roster',
      stage: '1. Build your coaching foundation',
      title: `Add your ${roster}`,
      description: `Add at least one player so plans can reflect real ${roster} size, positions, and needs.`,
      features: `${Roster} \u2022 Positions \u2022 CSV import`,
      path: '/roster',
      icon: '\uD83D\uDCCB',
      why: 'Good coaching starts with the players in front of you, not an idealised session copied from elsewhere.',
      goodLooksLike: 'Players, positions, and useful development context are accurate enough to adapt numbers and coaching points.',
      example: 'If only nine players attend, the plan can use 3v3 rotations instead of an unsuitable 6v6 activity.',
      commonMistake: 'Planning for the expected squad rather than the players actually available.',
    },
    {
      id: 'session',
      stage: '2. Plan and deliver',
      title: `Generate your first ${training} session`,
      description: 'Start with one observable learning objective, then review the generated progression before using it.',
      features: 'Objective \u2022 Progression \u2022 Coaching points \u2022 Adaptations',
      path: '/training',
      icon: '\uD83C\uDFC3',
      why: 'A focused objective lets you recognise whether learning occurred and prevents a session becoming disconnected activities.',
      goodLooksLike: 'Each activity increases challenge while preserving the same learning objective and giving players repeated decisions.',
      example: 'Objective: create a passing angle after releasing the ball. Progress from unopposed pairs to a directional small-sided game.',
      commonMistake: 'Choosing several unrelated objectives or accepting an AI plan without adapting it to the available players.',
    },
    {
      id: 'schedule',
      stage: '2. Plan and deliver',
      title: `Schedule the ${training}`,
      description: `Save the ${training} or ${match} so players, load, and preparation fit the real week.`,
      features: 'Calendar \u2022 Your local time \u2022 Conflict warnings',
      path: '/calendar',
      icon: '\uD83D\uDCC5',
      why: 'The same session can be appropriate or harmful depending on recovery time, match proximity, and competing commitments.',
      goodLooksLike: 'The date, start time, duration, and intensity make sense relative to the next match and recent workload.',
      example: `Use lower physical load close to a ${match}, while keeping tactical decisions specific to the upcoming challenge.`,
      commonMistake: 'Planning sessions in isolation without checking the surrounding week.',
    },
    {
      id: 'attendance',
      stage: '2. Plan and deliver',
      title: `Run ${training} and record attendance`,
      description: 'Mark at least one player, then use the timer and drill progression to adapt while coaching.',
      features: `${terms.todaySession || "Today's Training"} \u2022 Attendance \u2022 Timer \u2022 Session rating`,
      path: '/today-session',
      icon: '\u2705',
      why: 'Attendance and live observations explain why the delivered session may differ from the plan.',
      goodLooksLike: 'You adjust space, numbers, or rules when attendance or player response changes the intended challenge.',
      example: 'When two players are absent, reduce the area and use a neutral player instead of abandoning the objective.',
      commonMistake: 'Following the written plan exactly when the players need a different challenge.',
    },
    {
      id: 'reflect',
      stage: '3. Reflect and improve',
      title: `Complete ${training} with a reflection`,
      description: 'Add a rating and useful observations so the next session responds to evidence.',
      features: 'Session rating \u2022 Notes \u2022 AI debrief \u2022 Next-session tip',
      path: '/today-session',
      icon: '\uD83E\uDDE0',
      why: 'Improvement comes from comparing your intention with what players actually understood and performed.',
      goodLooksLike: 'Record one success, one difficulty, evidence you observed, and one specific change for next time.',
      example: 'Players created width but released the ball too slowly; next session will constrain touches during the first progression.',
      commonMistake: 'Judging the session only by enjoyment or the final score instead of the learning objective.',
    },
    {
      id: 'communicate',
      stage: '3. Reflect and improve',
      title: 'Ask, adapt, and communicate the next action',
      description: 'Use AI Coach or Club Chat successfully to refine an idea or align another coach.',
      features: 'AI Coach \u2022 Club Chat \u2022 Member Messages',
      path: '/chat',
      icon: '\uD83D\uDCAC',
      why: 'Reflection becomes improvement only when it changes the next decision and the right people understand that change.',
      goodLooksLike: 'Ask a specific question with context, evaluate the answer, then communicate one clear action to staff or members.',
      example: `Ask: "My U12s struggled to scan before receiving. Give me two ${training} adaptations and explain when to use each."`,
      commonMistake: 'Treating AI output as an instruction or sending long updates without a clear action.',
    },
  ];
}

export function getGuidedTourConfig({ experienceMode = 'full', terms = {}, canUse = () => true } = {}) {
  if (experienceMode === 'essentials') {
    return {
      id: 'essentials',
      version: ESSENTIALS_TOUR_VERSION,
      steps: essentialsTourSteps(terms, canUse),
    };
  }
  return { id: 'full', version: FULL_TOUR_VERSION, steps: FULL_TOUR_STEPS };
}

export function getJourneyConfig({ experienceMode = 'full', terms = {} } = {}) {
  if (experienceMode === 'essentials') {
    return {
      id: 'essentials',
      version: ESSENTIALS_JOURNEY_VERSION,
      title: 'Build your coaching cycle',
      steps: essentialsJourneySteps(terms),
    };
  }
  return {
    id: 'full',
    version: FULL_JOURNEY_VERSION,
    title: 'Getting the Most from EliteEdge AI',
    steps: FULL_JOURNEY_STEPS,
  };
}

export const ESSENTIALS_JOURNEY_STEP_IDS = Object.freeze([
  'foundation',
  'team',
  'roster',
  'session',
  'schedule',
  'attendance',
  'reflect',
  'communicate',
]);
